import { useFormatting } from "@msys/ui";
import type { GridColDef } from "@mui/x-data-grid-premium";
import React from "react";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import { DataGridDurationEditCell } from "../cells/DataGridDurationEditCell.js";
import type { ModifyItemCalculationFn } from "../useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";
import { makeCalculationValueSetter } from "../cells/helpers.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

function DurationCell({ value }: { value: number }) {
  const { getFormattedDuration } = useFormatting();
  return <span>{getFormattedDuration(value)}h</span>;
}

export function createEstimatedTimeColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "estimatedTime",
    headerName: options.headerName,
    width: 80,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    // timePerUnit is in minutes
    valueGetter: (_value, row) =>
      row.item?.proposedCalculation?.timePerUnit ?? 0,
    // estimatedTime (mutation) maps to timePerUnit (proposedCalculation)
    valueSetter: makeCalculationValueSetter<T>()(
      "timePerUnit",
      (itemId, value) =>
        options.modifyItemCalculation(itemId, { estimatedTime: value })
    ),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <DurationCell value={params.value ?? 0} />;
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <DataGridDurationEditCell {...params} />;
    },
  };
}
